<template>
  <div class="space-y-6" :class="fontClass">
    <div v-if="isLoading" class="flex items-center justify-center py-4">
      <Icon name="mdi:loading" class="h-6 w-6 animate-spin text-muted-foreground" />
    </div>

    <div v-else-if="!messages.length" class="text-center py-4 text-muted-foreground">
      No messages yet. Start a conversation!
    </div>

    <template v-else>
      <div
        v-for="(message, index) in displayMessages"
        :key="`${message.role}-${message.timestamp}-${index}`"
        class="flex flex-col gap-2"
        :class="message.role === 'assistant' ? 'justify-start' : 'justify-end'"
      >
        <div
          class="max-w-[85%] rounded-lg px-5 py-3 shadow-sm"
          :class="[
            message.role === 'assistant'
              ? 'bg-card text-card-foreground border border-border'
              : 'bg-primary text-white dark:bg-zinc-700 dark:border dark:border-zinc-600 ml-auto',
          ]"
        >
          <Suspense>
            <template #default>
              <MDCRenderer
                v-if="parsedContent.get(message.content)"
                :body="parsedContent.get(message.content).body"
                :data="parsedContent.get(message.content).data"
                class="prose prose-sm max-w-none break-words [&>:first-child]:mt-0 [&>:last-child]:mb-0 [&_img]:max-w-full [&_img]:max-h-80 [&_img]:object-contain [&_img]:rounded [&_img]:aspect-video [&_img]:bg-muted"
                :class="[
                  message.role === 'user'
                    ? 'prose-invert text-white [&_a]:text-white [&_a]:underline [&_code]:bg-zinc-600/50 dark:[&_code]:bg-zinc-600/50 [&_pre]:!bg-zinc-600/50 dark:[&_pre]:!bg-zinc-600/70 [&_a]:break-words'
                    : 'prose-stone dark:prose-invert [&_a]:break-words',
                ]"
              />
            </template>
            <template #fallback>
              <div class="animate-pulse">Loading...</div>
            </template>
          </Suspense>
        </div>
        <span
          class="text-xs text-muted-foreground/80"
          :class="message.role === 'assistant' ? 'ml-2' : 'mr-2 text-right'"
        >
          {{ toLocalString(message.timestamp) }}
        </span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { ChatMessage } from '@/types/chat'
import { computed, ref, watch } from 'vue'
import { parseMarkdown } from '@nuxtjs/mdc/runtime'
import { toLocalString } from '@/utils/dates'
import { useLogger } from '@/composables/useLogger'
import { useUserStore } from '@/stores/user'

const { log, warn, logError, debug } = useLogger()

const props = defineProps<{
  messages: ChatMessage[]
  isStreaming?: boolean
  isLoading?: boolean
}>()

const userStore = useUserStore()

// Compute font class based on user settings
const fontClass = computed(() => {
  switch (userStore.settings.fontPreference) {
    case 'serif':
      return 'font-serif'
    case 'monospace':
      return 'font-mono'
    default:
      return 'font-sans' // Default to sans-serif
  }
})

// Add refs for parsed content
const parsedContent = ref(new Map())

// Format and cache parsed messages
const formatMessage = async (content: string | undefined) => {
  try {
    if (!content) {
      debug('Empty content received in formatMessage')
      return { body: '', data: {} }
    }

    // Fix code block handling during streaming
    if (props.isStreaming && typeof content === 'string') {
      // Handle multiline code blocks
      const codeBlockMatches = content.match(/```[\s\S]*?```|```[\s\S]*$/g)
      if (codeBlockMatches) {
        for (const match of codeBlockMatches) {
          if (!match.endsWith('```')) {
            debug('Fixing unclosed code block in streaming content')
            content = content.replace(match, `${match}\n\`\`\``)
          }
        }
      }

      // Handle inline code blocks
      const inlineCodeMatches = content.match(/`[^`]*`|`[^`]*$/g)
      if (inlineCodeMatches) {
        for (const match of inlineCodeMatches) {
          if (!match.endsWith('`')) {
            debug('Fixing unclosed inline code block')
            content = content.replace(match, `${match}\``)
          }
        }
      }
    }

    const parsed = await parseMarkdown(content)

    // Validate parsed output
    if (!parsed || (!parsed.body && content)) {
      warn('Markdown parsing produced empty result for non-empty content', {
        content: content.substring(0, 100),
        parsed,
      })
    }

    return parsed
  } catch (error) {
    logError('Error formatting message:', error, {
      content: content?.substring(0, 100),
      type: typeof content,
    })
    // Return a more visible error state in development
    if (import.meta.dev) {
      return {
        body: `<div class="text-red-500">Error parsing markdown: ${error instanceof Error ? error.message : 'Unknown error'}</div>`,
        data: {},
      }
    }
    return { body: content || '', data: {} }
  }
}

// Pre-parse messages when they change
watch(
  () => props.messages,
  async (messages) => {
    // Ensure messages is an array before filtering
    const messageArray = Array.isArray(messages) ? messages : []

    // Get only new messages that need parsing
    const newMessages = messageArray.filter(
      (msg) => msg?.content && !parsedContent.value.has(msg.content)
    )

    // Parse all new messages in parallel
    await Promise.all(
      newMessages.map(async (msg) => {
        if (msg?.content) {
          const parsed = await formatMessage(msg.content)
          parsedContent.value.set(msg.content, parsed)
        }
      })
    )

    log('🎯 ChatMessages updated:', {
      count: messageArray?.length,
      messages: messageArray?.map((m) => ({
        role: m?.role,
        content: m?.content?.substring(0, 20) + '...',
        timestamp: m?.timestamp,
      })),
    })
  },
  { immediate: true }
)

// Update displayMessages computed to ensure UTC sorting
const displayMessages = computed(() => {
  // Ensure we have an array to work with
  const messageArray = Array.isArray(props.messages) ? props.messages : []

  // Create a Map to track unique messages by content
  const uniqueMessages = new Map()

  messageArray.forEach((message) => {
    if (!message) return
    const key = `${message.role}-${message.content}`
    // Keep the most recent message if duplicate
    if (
      !uniqueMessages.has(key) ||
      new Date(message.timestamp + 'Z').getTime() >
        new Date(uniqueMessages.get(key).timestamp + 'Z').getTime()
    ) {
      uniqueMessages.set(key, message)
    }
  })

  // Convert Map values to array and sort
  const messages = Array.from(uniqueMessages.values())
  return messages.sort((a, b) => {
    const aTime = new Date(a.timestamp + 'Z').getTime()
    const bTime = new Date(b.timestamp + 'Z').getTime()
    return aTime - bTime
  })
})
</script>
